import React from 'react';
import { Calendar, Clock, User, ArrowRight, Tag } from 'lucide-react';
import { blogData } from '../data/blogData';

interface BlogPageProps {
  onSelectPost: (slug: string) => void;
}

export const BlogPage: React.FC<BlogPageProps> = ({ onSelectPost }) => {
  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-12 space-y-16">
      
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <span>ENGINEERING INSIGHTS & THOUGHT LEADERSHIP</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            Perspectives on Modern Software, AI & Growth
          </h1>
          <p className="text-base sm:text-lg text-[#D5DFEC] leading-relaxed">
            In-depth technical guides, architectural comparisons, and digital strategies written by engineers and technology directors.
          </p>
        </div>
      </section>

      {/* Featured / Articles Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {blogData.map((post) => (
            <article
              key={post.id}
              onClick={() => onSelectPost(post.slug)}
              className="rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] overflow-hidden hover:border-[#159DE8]/50 transition-all duration-300 shadow-card-dark flex flex-col justify-between cursor-pointer group"
            >
              <div>
                <div className="relative aspect-[16/10] overflow-hidden bg-[#050A13]">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c1a2d] via-transparent to-transparent opacity-80" />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-md bg-[#050A13]/90 backdrop-blur-md border border-[#1b2f4a] text-[11px] font-semibold text-[#FF851B]">
                      {post.category}
                    </span>
                  </div>
                </div>

                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-4 text-[11px] text-[#8FA0B8]">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {post.publishDate}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {post.readTime}
                    </span>
                  </div>

                  <h2 className="text-lg font-bold text-white group-hover:text-[#FF851B] transition-colors font-display line-clamp-2">
                    {post.title}
                  </h2>

                  <p className="text-xs sm:text-sm text-[#8FA0B8] leading-relaxed line-clamp-3">
                    {post.summary}
                  </p>
                </div>
              </div>

              <div className="p-6 pt-0 border-t border-[#1b2f4a]/50 mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-[#D5DFEC]">
                  <div className="w-6 h-6 rounded-full bg-[#0B2342] text-[#159DE8] flex items-center justify-center font-bold text-[10px] border border-[#1b2f4a]">
                    {post.author.charAt(0)}
                  </div>
                  <span className="text-[11px] font-medium">{post.author}</span>
                </div>

                <span className="text-xs font-bold text-[#159DE8] group-hover:text-[#FF851B] flex items-center gap-1 transition-colors">
                  <span>Read Article</span>
                  <ArrowRight size={13} />
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>

    </div>
  );
};
